import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { strategicPillarService } from '@/lib/api/services/strategic-pillar'
import type { StrategicPillarFilterParams, PaginationParams, CreateStrategicPillar, UpdateStrategicPillar, StrategicPillarStats, StrategicPillarStatsResponse } from '@/lib/api/types'


export const strategicPillarKeys = {
  all: ['strategic-pillar'] as const,
  strategicPillars: () => [...strategicPillarKeys.all, 'strategic-pillars'] as const,
  strategicPillar: (id: string) => [...strategicPillarKeys.strategicPillars(), id] as const,
  strategicPillarsPaginated: (params: PaginationParams, filters: StrategicPillarFilterParams) =>
    [...strategicPillarKeys.strategicPillars(), 'paginated', params, filters] as const,
  strategicPillarsSearch: (query: string, params: PaginationParams) => [...strategicPillarKeys.strategicPillars(), 'search', query, params] as const,
  stats: () => [...strategicPillarKeys.all, 'stats'] as const,
}


export const useStrategicPillars = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {},
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: strategicPillarKeys.strategicPillarsPaginated(params, filters),
    queryFn: () => strategicPillarService.getStrategicPillars(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
    enabled: enabled
  })
}


export const useStrategicPillar = (strategicPillarId: string) => {
  return useQuery({
    queryKey: strategicPillarKeys.strategicPillar(strategicPillarId),
    queryFn: () => strategicPillarService.getStrategicPillarById(strategicPillarId),
    enabled: !!strategicPillarId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}



export const useStrategicPillarStats = () => {
  return useQuery<StrategicPillarStatsResponse, Error, StrategicPillarStats>({
    queryKey: strategicPillarKeys.stats(),
    queryFn: () => strategicPillarService.getStrategicPillarStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}


export const useCreateStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateStrategicPillar) => strategicPillarService.createStrategicPillar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar creation failed:', error)
    }
  })
}


export const useUpdateStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ strategicPillarId, data }: { strategicPillarId: string; data: UpdateStrategicPillar }) =>
      strategicPillarService.updateStrategicPillar(strategicPillarId, data),
    onSuccess: (_response, { strategicPillarId }) => {
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillar(strategicPillarId) })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar update failed:', error)
    }
  })
}


export const useUpdateStrategicPillarStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ strategicPillarId, status }: { strategicPillarId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      strategicPillarService.updateStrategicPillarStatus(strategicPillarId, status),
    onSuccess: (response, { strategicPillarId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(strategicPillarKeys.strategicPillar(strategicPillarId), response.data)
      }
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar status update failed:', error)
    }
  })
}


export const useDeleteStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (strategicPillarId: string) => strategicPillarService.deleteStrategicPillar(strategicPillarId),
    onSuccess: (_, strategicPillarId) => {
      queryClient.removeQueries({ queryKey: strategicPillarKeys.strategicPillar(strategicPillarId) })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar deletion failed:', error)
    }
  })
}

export const useSearchStrategicPillars = (
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: strategicPillarKeys.strategicPillarsSearch(searchBy || '', params),
    queryFn: () => strategicPillarService.searchStrategicPillars(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

/**
 * Hook to fetch strategic pillar highlights
 */
export const useStrategicPillarHighlights = (type: string = 'HOME_PAGE_PRIORITIES_OF_AFSA') => {
  return useQuery({
    queryKey: [...strategicPillarKeys.all, 'highlights', type],
    queryFn: () => strategicPillarService.getStrategicPillarHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to update strategicPillar highlights
 */
export const useUpdateStrategicPillarHighlights = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (strategicPillarIds: string[]) =>
      strategicPillarService.updateStrategicPillarHighlights(strategicPillarIds),
    onSuccess: () => {
      // Invalidate highlights query to refetch
      queryClient.invalidateQueries({ queryKey: [...strategicPillarKeys.all, 'highlights'] })
    },
    onError: (error) => {
      console.error('Failed to update highlights:', error)
    }
  })
}
